import { z } from 'zod';

import { defineService } from '@@api/lib/defineService';
import { UnityBreakingNews } from '@@api/services/breakingnews/schemas';
import {
    Article,
    ArticleTemplate,
    DetailedUnityRevision,
    LiquifiedArticle,
    Liquify,
    NewArticle,
    Revision,
    UnityArticle,
    UnityCompare,
} from '@@api/services/content/schemas/article';
import { Comment } from '@@api/services/content/schemas/comment';
import {
    deserializeArticle,
    deserializeDetailedRevision,
    serializeArticle,
} from '@@api/services/content/transformers';
import { CreationMetadata } from '@@api/services/metadata/schemas';
import config from '@@config';
import { decodeCompare } from '@@plate/serialization/unity/Compare';

export const contentService = defineService(
    (defineEndpoint) => ({
        article: {
            get: defineEndpoint({
                method: 'GET',
                path: '/articles/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                responses: {
                    200: UnityArticle.transform(deserializeArticle),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/articles?useTemplate=true',
                query: z.object({
                    targetLanguage: z.string().optional(),
                    contentLocale: z.string().optional(),
                }),
                body: NewArticle.transform(serializeArticle),
                responses: {
                    201: UnityArticle.transform(deserializeArticle),
                },
            }),
            clone: defineEndpoint({
                method: 'POST',
                path: '/articles',
                query: z.object({
                    sourceId: UnityArticle.shape.id.optional(),
                    targetLanguage: z.string().optional(),
                    contentLocale: z.string().optional(),
                }),
                body: z.object({
                    metadata: CreationMetadata,
                }),
                responses: {
                    201: UnityArticle.transform(deserializeArticle),
                },
            }),
            liquify: defineEndpoint({
                method: 'POST',
                path: '/articles?liquify=true&async=true',
                query: z.object({
                    sourceId: UnityArticle.shape.id,
                }),
                body: Liquify,
                responses: {
                    201: z.array(LiquifiedArticle),
                    202: z.array(z.never()),
                },
            }),
            getLiquid: defineEndpoint({
                method: 'GET',
                path: '/articles/:id/liquid',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                responses: {
                    200: z.array(LiquifiedArticle),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/articles/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                body: Article.transform(serializeArticle),
                responses: {
                    200: UnityArticle.transform(deserializeArticle),
                    412: UnityCompare,
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/articles/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                responses: {
                    204: z.never(),
                },
            }),
            replace: defineEndpoint({
                method: 'POST',
                path: '/articles/:id/replace',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                query: z.object({
                    breakingnewsId: UnityBreakingNews.shape.id,
                }),
                body: z.undefined(),
                responses: {
                    200: z.object({ resourceId: z.string() }),
                },
            }),
        },
        articleDiff: {
            get: defineEndpoint({
                method: 'GET',
                path: '/articles/:id/diff',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                query: z.object({
                    origin: z.literal('MASTER'),
                    derivative: Revision.shape.id,
                }),
                responses: {
                    200: UnityCompare.transform((payload) => decodeCompare(payload)),
                },
            }),
        },
        revisions: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/articles/:id/revisions',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                responses: {
                    200: z.array(Revision),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/articles/:articleId/revisions/:revisionId',
                pathParams: z.object({
                    articleId: UnityArticle.shape.id,
                    revisionId: Revision.shape.id,
                }),
                responses: {
                    200: DetailedUnityRevision.transform(deserializeDetailedRevision),
                },
            }),
        },
        templates: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/templates',
                responses: {
                    200: z.array(ArticleTemplate),
                },
            }),
        },
        comments: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/articles/:id/comments',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                responses: {
                    200: z.array(Comment),
                },
            }),
            postCommentResponse: defineEndpoint({
                method: 'POST',
                path: '/articles/:articleId/comments/:commentId/comments',
                pathParams: z.object({
                    articleId: UnityArticle.shape.id,
                    commentId: Comment.shape.id,
                }),
                body: Comment.pick({ text: true }),
                responses: {
                    201: Comment,
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/articles/:id/comments',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                body: Comment.pick({ text: true }),
                responses: {
                    201: Comment,
                },
            }),
        },
    }),
    {
        baseUrl: config.apiEnvVars.contentUrl,
    },
);
